import React, { useMemo, useEffect, useState } from 'react'
import {
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  TableHead,
  Tooltip,
} from '@mui/material'
import Fuse from 'fuse.js'
import ErrorMessage from '../../component/shared/component/ErrorMessage'
import { Link } from 'react-router-dom'
import SummarizeIcon from '@mui/icons-material/Summarize'
import { TablePaginationActions } from './TablePaginationActions'
import { LoadingSkeletonRows } from './LoadingSkeletonRows'

export const PaginationTable = ({
  searchFilter,
  linkBase,
  columns = [],
  useGetPoints,
  salesIds = [],
  tableSx,
}) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  useEffect(() => {
    setPage(0)
  }, [salesIds, searchFilter])

  const { data = [], isLoading } = useGetPoints({ salesIds })

  const pointData = useMemo(() => {
    if (!searchFilter) return data

    const itemFuse = new Fuse(data, {
      shouldSort: true,
      includeScore: true,
      threshold: 0.3,
      ignoreLocation: true,
      keys: ['companyName'],
    })

    const filtereditemFuse = itemFuse.search(searchFilter)

    return filtereditemFuse
      .filter((fuse) => fuse.score < 0.5)
      .map((each) => each.item)
      .sort((a, b) => b.totalPoint - a.totalPoint)
  }, [searchFilter, data])

  const arr = useMemo(() => {
    return rowsPerPage > 0
      ? [...pointData].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : pointData
  }, [page, rowsPerPage, pointData])

  if (arr.length === 0 && !isLoading)
    return (
      <Paper>
        <ErrorMessage empty message={'No result to display'} />
      </Paper>
    )

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 500, ...tableSx }} aria-label='custom pagination table'>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.id} sx={col.headerSx}>
                {col.label}
              </TableCell>
            ))}
            {linkBase && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <LoadingSkeletonRows rows={10} length={columns.length + (linkBase ? 1 : 0)} />
          ) : (
            arr.map((row, idx) => (
              <TableRow key={idx}>
                {columns.map((col) => (
                  <TableCell key={col.id}>{col.view ? col.view(row) : row[col.id]}</TableCell>
                ))}
                {linkBase && (
                  <TableCell>
                    <Link to={`/${linkBase}?id=${row.companyAccountId}`}>
                      <Tooltip title={`View Summary for ${row.companyAccountId}`}>
                        <IconButton>
                          <SummarizeIcon />
                        </IconButton>
                      </Tooltip>
                    </Link>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[10, 20, 30, { label: 'All', value: -1 }]}
              colSpan={columns.length + 1}
              count={pointData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{ inputProps: { 'aria-label': 'rows per page' }, native: true }}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10))
                setPage(0)
              }}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  )
}
